import { useEffect, useState, useRef } from "react";
import { Bell, Zap, Activity, CheckCheck, Gavel, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";

export const EngineActivity = () => {
  const [a, setA] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/matching/activity").then(({ data }) => alive && setA(data)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (!a) return null;
  const item = (label, value, tone = "text-navy") => (
    <div className="rounded-xl border border-border bg-white px-4 py-3">
      <p className={`text-xl font-serif ${tone}`}>{value}</p>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
  return (
    <div className="rounded-2xl border border-border bg-gradient-to-br from-navy to-navy-light p-5 text-white" data-testid="engine-activity">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-gold" />
          <span className="font-serif text-lg">Recruitment Engine Activity</span>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${a.auto_mode ? "bg-success/20 text-green-200" : "bg-white/10 text-white/70"}`} data-testid="engine-auto-state">
          <Activity className="h-3.5 w-3.5" /> Auto-Mode: {a.auto_mode ? "ON" : "OFF"}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {item("Matches Today", a.matches_today, "text-gold-light")}
        {item("Awaiting Review", a.pending, "text-gold-light")}
        {item("Queue: Waiting", a.queue_queued)}
        {item("Queue: Processing", a.queue_processing)}
        {item("Candidates Ready", a.candidates_ready)}
        {item("Jobs Ready", a.jobs_ready)}
      </div>
      <p className="mt-3 text-[11px] text-white/50">{a.workers} durable workers · Judge DB vault live</p>
    </div>
  );
};

export const NotificationBell = ({ onOpenMatch }) => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const load = () => api.get("/admin/notifications").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, []);
  const markAll = async () => { await api.post("/admin/notifications/read-all"); load(); };
  const openItem = (n) => {
    if (n.match_id && onOpenMatch) {
      onOpenMatch(n.match_id);
      setOpen(false);
    }
  };
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen((v) => !v); if (!open && data.unread) markAll(); }} className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/20 text-white hover:bg-white/10" data-testid="notif-bell" aria-label="Notifications">
        <Bell className="h-4 w-4" />
        {data.unread > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-white" data-testid="notif-badge">{data.unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-80 rounded-xl border border-border bg-white shadow-xl" data-testid="notif-panel">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="font-serif text-navy">Match Alerts</span>
            <button onClick={markAll} className="inline-flex items-center gap-1 text-xs font-semibold text-navy hover:text-gold" data-testid="notif-mark-read"><CheckCheck className="h-3.5 w-3.5" /> Mark all read</button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {data.items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">No alerts yet.</p>
            ) : data.items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => openItem(n)}
                className={`flex w-full items-start gap-2 border-b border-border/60 px-4 py-3 text-left transition-colors hover:bg-gold/10 ${!n.read ? "bg-gold/5" : ""} ${n.match_id ? "cursor-pointer" : "cursor-default"}`}
                data-testid={`notif-item-${n.id}`}
              >
                <Gavel className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-charcoal">{n.message}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</p>
                  {n.match_id && <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-navy">Review match <ChevronRight className="h-3 w-3" /></p>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
